/**
 * Thin PostHog analytics wrapper.
 *
 * Activates only when REACT_APP_POSTHOG_KEY is set AND the user has
 * accepted analytics cookies (cookie consent === "all"). Otherwise
 * every function is a no-op.
 *
 * Use `track(event, props)` for custom events and `identify(user)` after
 * login. The API mirrors PostHog so a future swap to plausible / mixpanel
 * is cheap.
 */
import { hasAnalyticsConsent } from "@/components/CookieConsent";

let _ph = null;
let _booted = false;

async function ensureBooted() {
  if (_booted) return _ph;
  if (!hasAnalyticsConsent()) return null;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return null;
  _booted = true;
  try {
    const mod = await import("posthog-js");
    const ph = mod.default || mod;
    ph.init(key, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
      capture_pageview: true,
      person_profiles: "identified_only",
      // Session replay is gated server-side in PostHog → Project →
      // Replay → "Record web sessions". When enabled there, clients
      // automatically start recording. We mask all input text by
      // default so passwords, microchip numbers, and DOB never leave
      // the browser DOM unredacted.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: ".ph-no-capture, .password-field, [data-private]",
      },
    });
    // Expose for cross-tool integrations (Sentry) — the SDK already
    // attaches itself to window.posthog by default, but we re-set
    // explicitly so the reference is reliable across consent flows.
    if (typeof window !== "undefined") window.posthog = ph;
    _ph = ph;
    return _ph;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] posthog-js not installed; install it to enable analytics.", e?.message || e);
    return null;
  }
}

export async function bootAnalytics() {
  await ensureBooted();
}

export async function track(event, props = {}) {
  const ph = await ensureBooted();
  if (ph?.capture) {
    try {
      ph.capture(event, props);
    } catch {
      /* swallow */
    }
  }
}

export async function identify(user) {
  const ph = await ensureBooted();
  if (ph?.identify && user?.id) {
    try {
      ph.identify(user.id, { email: user.email, name: user.name, role: user.role });
    } catch {
      /* swallow */
    }
  }
}

export async function reset() {
  if (_ph?.reset) {
    try {
      _ph.reset();
    } catch {
      /* swallow */
    }
  }
}

// Re-boot analytics when user accepts the cookie banner mid-session.
if (typeof window !== "undefined") {
  window.addEventListener("cookie-consent-changed", (e) => {
    if (e.detail === "all") ensureBooted();
  });
}
